import {
  Component,
  OnInit,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  Inject,
  OnDestroy,
} from '@angular/core';
import {
  Aggregation,
  BucketItem,
  FilterGroup,
  Filters,
  FilterItem,
  ChipsValues,
  FilterSelection,
} from '@search/interfaces/insights.interface';
import { FiltersService } from '@search/services/filters.service';
import { DisplayOptions } from '@marketing/insights/insights.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { Location as ngCommonLocation } from '@angular/common';
// Using alias to prevent type collision
import { LOCATION } from '@ng-web-apis/common';
import {
  AnalyticsService,
  FrkBreakpoint,
  ResponsiveService,
} from '@frk/eds-components';
import { Subscription } from 'rxjs';

interface SelectedFilterData {
  label: string;
  count: number;
  value: string;
}
@Component({
  selector: 'ft-insights-filters',
  templateUrl: './insights-filters.component.html',
})
export class InsightsFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() aggregation: Aggregation;
  @Input() translationLabel: any;
  @Input() displayOptions: DisplayOptions;
  @Output() filtered = new EventEmitter();
  @Output() searchTerm = new EventEmitter();
  filterGroup: FilterGroup = {};
  selectedPreFilters: string;
  searchElement: string;
  private setPreFiltersOnLoad = true;
  public verticalFilter: boolean;
  private isVerticalFilter$: Subscription;
  private initialAggregation: Aggregation;
  constructor(
    public filters: FiltersService,
    private route: ActivatedRoute,
    private router: Router,
    private location: ngCommonLocation,
    @Inject(LOCATION) readonly locationRef: Location,
    private analytics: AnalyticsService,
    private responsiveService: ResponsiveService
  ) {}

  ngOnInit(): void {
    this.isVerticalFilter$ = this.responsiveService
      .getBreakpointObs$()
      .subscribe((bp: FrkBreakpoint) => {
        this.verticalFilter = [
          FrkBreakpoint.Mobile,
          FrkBreakpoint.Tablet,
        ].includes(bp);
      });
    this.filterGroup.filters = [];
    this.filterGroup.chipsFilter = [];
    this.filterGroup.search = {
      fieldId: 'enhancedSearch',
      placeholder: this.getTranslation('searchLabel'),
    };
    const urlParam = this.route?.snapshot?.queryParams?.filter;
    if (urlParam) {
      this.selectedPreFilters = urlParam.split(';');
      this.arrangeUrlSelected();
    }
  }

  ngOnChanges(): void {
    if (Object.entries(this.aggregation).length && this.setPreFiltersOnLoad) {
      const tempArray = Object.entries(this.aggregation);
      if (tempArray.length) {
        tempArray.forEach(([element]) => {
          if (element === 'assetclassPage') {
            tempArray.unshift(
              tempArray.splice(
                tempArray.findIndex(
                  (item) => item[element] === 'assetclassPage'
                ),
                1
              )[0]
            );
          }
        });
        this.aggregation = Object.fromEntries(tempArray);
      }

      this.initialAggregation = this.aggregation;
      Object.entries(this.aggregation).forEach(([filterGroupName]) => {
        if (
          filterGroupName !== 'investmentteam' ||
          this.displayOptions?.enableSIMsFilter
        ) {
          this.processFilterGroup(
            filterGroupName,
            this.aggregation[filterGroupName].buckets
          );
        }
      });
      this.setPreFiltersOnLoad = false;
    }
  }

  /**
   * Processes the filter group by converting the buckets into filter items and updating the filter group accordingly.
   * This method will process filterGroups for enhjanced filter.
   * @param filterGroupName - The name of the filter group.
   * @param buckets - The array of bucket items.
   * @returns void
   */
  processFilterGroup(filterGroupName: string, buckets: BucketItem[]): void {
    const filterItems: object[] = [];
    buckets.forEach((bucket: BucketItem) => {
      const filterItem = {
        label: this.getTranslation(bucket.key),
        count: bucket.doc_count,
        checked: this.filters.activeFilters[
          filterGroupName + '.exact'
        ]?.includes(bucket.key)
          ? true
          : false, // we don't want to return undefined
        value: bucket.key,
      };
      filterItems.push(filterItem);
    });

    const filterLengthToCheck = this.displayOptions?.enableSIMsFilter ? 3 : 2;

    if (this.filterGroup.filters.length < filterLengthToCheck) {
      this.filterGroup.filters.push({
        name: filterGroupName,
        title: this.getTranslation(filterGroupName),
        label: this.getTranslation(filterGroupName),
        selectedText: 'selected',
        items: filterItems,
        showCount: this.displayOptions.showCount,
        linkUrl: this.locationRef.href,
        alignment: 'right',
      });
    } else {
      this.filterGroup.filters.forEach((element: Filters, index) => {
        if (element.name === filterGroupName) {
          this.filterGroup.filters[index].items = filterItems;
        }
      });
    }
  }

  /**
   * Updates the search term and performs necessary actions.
   * @param searchTerm - The search term to be used for filtering.
   */
  onTermSearch(searchTerm: string) {
    this.searchElement = searchTerm;
    this.filterGroup.search.searchParam = this.searchElement;
    this.updateUrl(this.searchElement);
    this.searchTerm.emit(searchTerm);
  }

  /**
   * Handles the selection of a filter.
   * @param selectedFilter - The selected filter.
   */
  onSelectFilter(selectedFilter: FilterSelection) {
    const type: string = selectedFilter.name;
    const value: Array<SelectedFilterData> = selectedFilter.selected;

    this.filters.addFilter(type + '.exact', value);

    this.addChips(type, value);

    if (this.filters.getFilters().indexOf('[]') === 0) {
      this.filterGroup.chipsFilter = [];
    }
    this.updateUrl(this.searchElement);
    this.filtered.emit();
  }

  addChips(filterType: string, selectedValues: Array<FilterItem>) {
    const seletedChips: Array<ChipsValues> = [];
    selectedValues.forEach((element: FilterItem) => {
      seletedChips.push({
        label: this.getTranslation(filterType),
        value: this.getTranslation(element.value),
        name: filterType,
        valueName: element.value,
      });
    });

    const currentIndex = this.filterGroup.chipsFilter.findIndex(
      (currentChips) => currentChips.groupName === filterType
    );

    currentIndex < 0
      ? this.filterGroup.chipsFilter.push({
          groupName: filterType,
          chips: seletedChips,
        })
      : (this.filterGroup.chipsFilter[currentIndex] = {
          groupName: filterType,
          chips: seletedChips,
        });
  }

  chipRemoved(chipValue: ChipsValues) {
    const chipType = chipValue.name + '.exact';

    const removedChipIndex = this.filters.activeFilters[chipType].indexOf(
      chipValue.valueName
    );

    this.filters.activeFilters[chipType].splice(removedChipIndex, 1);
    if (this.filters.getFilters().indexOf('[]') === 0) {
      this.filterGroup.chipsFilter = [];
    }
    this.filters.removeFilter(chipType, chipValue.valueName);
    this.updateUrl(this.searchElement);
    this.analytics.trackEvent({
      event: 'pillClick',
      detailed_event: 'Pill Clicks',
      action: 'Remove',
      label: chipValue.value,
      event_data: {
        action: 'Remove',
        category: '',
        label: chipValue.value,
        link_id: '',
        link_text: '',
        link_type: this.analytics.getLinkType(''),
        link_url: '',
      },
    });
    this.filtered.emit();
    this.filterDropdownUpdate();
  }

  resetFilter(isReset: boolean) {
    if (isReset) {
      this.filters.activeFilters = [];
      this.filterGroup.chipsFilter = [];
      this.filterGroup.search.searchParam = '';
      this.searchElement = '';
      this.searchTerm.emit('*');
      this.filtered.emit();
      this.updateUrl(this.searchElement, true);
    }
    this.analytics.trackEvent({
      event: 'form_filter',
      name: 'reset',
      label: this.locationRef.href,
    });
  }

  getTranslation(label: string) {
    if (Object.keys(this.translationLabel).length) {
      return this.translationLabel[label]
        ? this.translationLabel[label]
        : label;
    } else {
      return label;
    }
  }

  updateUrl(searchterm: any, reset?: boolean) {
    const selected = this.getSelectedFilters(searchterm);
    const updateUrl = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: selected ? { filter: selected } : null,
        queryParamsHandling: reset ? '' : 'preserve',
      })
      .toString();
    this.location.go(updateUrl);
  }

  getSelectedFilters(searchterm: any) {
    const selectedthemefilters = JSON.parse(this.filters.getFilters()).map(
      (filt) =>
        filt.fieldName.split('.')[0].toLowerCase() + ':' + filt.fieldValue
    );

    const selectedSearchQuery: string[] = searchterm
      ? [searchterm ? 'searchquery:' + searchterm.toLowerCase() : null]
      : [null];
    const selectedfilters = [...selectedthemefilters, ...selectedSearchQuery];
    return selectedfilters.length ? selectedfilters.join(';') : null;
  }

  arrangeUrlSelected() {
    let searchQuery = '';
    let selectedFilterItems: any[] = [];
    for (const index of this.selectedPreFilters) {
      selectedFilterItems = [];
      const label = index.split(':')[0];
      if (label !== '') {
        switch (label) {
          case 'searchquery': {
            searchQuery = index.split(':')[1];
            break;
          }
          default: {
            index
              .split(':')[1]
              .split(',')
              .forEach((filterItemValue: string) => {
                selectedFilterItems.push({
                  label: this.getTranslation(filterItemValue),
                  value: filterItemValue,
                });
              });

            if (label === 'assetclasspage') {
              this.filters.addFilter(
                'assetclassPage.exact',
                selectedFilterItems
              );
              this.addChips('assetclassPage', selectedFilterItems);
            } else {
              this.filters.addFilter(label + '.exact', selectedFilterItems);
              this.addChips(label, selectedFilterItems);
            }
          }
        }
      }
    }
    this.onTermSearch(searchQuery);
  }
  ngOnDestroy(): void {
    this.isVerticalFilter$.unsubscribe();
  }

  private filterDropdownUpdate(): void {
    Object.entries(this.initialAggregation).forEach(([filterGroupName]) => {
      if (
        filterGroupName !== 'investmentteam' ||
        this.displayOptions?.enableSIMsFilter
      ) {
        this.processFilterGroup(
          filterGroupName,
          this.initialAggregation[filterGroupName].buckets
        );
      }
    });
  }
}
